/**
 * Hash-based SPA router
 *
 * Hash routing so the site works from any static host and any file:// copy
 * without server rewrites. Routes lazy-import their page module, so the palm
 * pipeline is not downloaded by someone who only wanted a pañcāṅga.
 *
 * ◆ Each page module exports `render(mount, params)` and optionally
 * `teardown()`. The router calls `teardown` before swapping — which is what
 * gives components a chance to unsubscribe from the store. Without it, ten
 * navigations leave ten dead listeners updating detached DOM.
 *
 * © Manish Jagdish Thatte
 */

import { t } from './utils/i18n.js';

const ROUTES = {
  '': () => import('./pages/home.js'),
  kundali: () => import('./pages/kundali.js'),
  panchang: () => import('./pages/panchang.js'),
  muhurta: () => import('./pages/muhurta.js'),
  varga: () => import('./pages/varga.js'),
  refinements: () => import('./pages/refinements.js'),
  strength: () => import('./pages/strength.js'),
  dasha: () => import('./pages/dasha.js'),
  yogas: () => import('./pages/yogas.js'),
  transit: () => import('./pages/transit.js'),
  matching: () => import('./pages/matching.js'),
  tajaka: () => import('./pages/tajaka.js'),
  longevity: () => import('./pages/longevity.js'),
  remedies: () => import('./pages/remedies.js'),
  rectify: () => import('./pages/rectify.js'),
  palm: () => import('./pages/palm.js'),
  learn: () => import('./pages/learn.js'),
  mundane: () => import('./pages/mundane.js'),
  reading: () => import('./pages/reading.js'),
  correlation: () => import('./pages/correlation.js'),
  sources: () => import('./pages/sources.js'),
  offerings: () => import('./pages/offerings.js'),
  saved: () => import('./pages/saved.js'),
  settings: () => import('./pages/settings.js'),
  about: () => import('./pages/about.js'),
  signin: () => import('./pages/signin.js'),
  signup: () => import('./pages/signup.js'),
  account: () => import('./pages/account.js'),
  'order-reading': () => import('./pages/order-reading.js'),
  console: () => import('./pages/console.js'),
  delivered: () => import('./pages/delivered.js'),
  legal: () => import('./pages/legal.js'),
};

let currentPage = null;
let mount = null;
let navigationToken = 0;

export function start(mountElement) {
  mount = mountElement;
  window.addEventListener('hashchange', route);
  return route();
}

export function stop() {
  window.removeEventListener('hashchange', route);
}

/** `#/kundali/abc` → `{ name: 'kundali', params: ['abc'] }`. */
export function parseHash(hash = location.hash) {
  const path = String(hash).replace(/^#\/?/, '');
  const [name, ...params] = path.split('/').filter(Boolean);
  return { name: name ?? '', params };
}

export async function route() {
  if (!mount) return;
  const { name, params } = parseHash();

  // ◆ `#/saved/<id>` bookmarks open the chart on kundali. Rewrite before any
  // page load so we never paint the list and so a replace mid-render cannot
  // race the next navigation's teardown.
  if (name === 'saved' && params[0]) {
    location.replace(`#/kundali/${encodeURIComponent(params[0])}`);
    return;
  }

  // ⚠ A token, because page modules are loaded asynchronously and a user can
  // click twice. Without it a slow import can finish after a later one and
  // render the previous page over the current one.
  const token = ++navigationToken;

  try {
    currentPage?.teardown?.();
  } catch (err) {
    console.error('[router] teardown threw:', err);
  }
  currentPage = null;
  mount.textContent = '';

  const { routeAccess } = await import('./utils/access.js');
  const access = routeAccess(name);
  if (!access.allowed) {
    if (token !== navigationToken) return;
    return renderPaywall(name);
  }

  const load = ROUTES[name] ?? (() => import('./pages/not-found.js'));

  let page;
  try {
    page = await load();
  } catch (err) {
    if (token !== navigationToken) return;
    // ⚠ A failed chunk load is usually a stale service worker after a deploy,
    // not a bug. Say something actionable instead of leaving a blank page.
    return renderLoadError(err);
  }
  if (token !== navigationToken) return;

  currentPage = page;
  try {
    await page.render(mount, params);
  } catch (err) {
    if (token !== navigationToken) return;
    console.error(`[router] page "${name}" failed to render:`, err);
    renderPageError(err);
  }
  if (token !== navigationToken) return;

  // Charts and tables must not be mangled by browser page-translate.
  const { protectTechnicalMarkup } = await import('./utils/browser-translate.js');
  protectTechnicalMarkup(mount);

  // Move focus to the page heading so a keyboard or screen-reader user is not
  // left at the top of the nav after every navigation.
  mount.focus?.();
  window.scrollTo({ top: 0 });
}

function renderPaywall(routeName) {
  mount.textContent = '';
  const box = document.createElement('div');
  box.className = 'notice notice--warning';
  const h = document.createElement('h1');
  h.textContent = t('paywall.title');
  const p = document.createElement('p');
  p.textContent = t('paywall.body', { route: routeName });
  const a = document.createElement('a');
  a.className = 'button';
  a.href = '#/offerings';
  a.textContent = t('paywall.cta');
  box.append(h, p, a);
  mount.append(box);
  mount.focus?.();
  window.scrollTo({ top: 0 });
}

export function navigate(path) {
  location.hash = `#/${String(path).replace(/^\/+/, '')}`;
}

function renderLoadError(err) {
  mount.textContent = '';
  const box = document.createElement('div');
  box.className = 'notice notice--warning';
  const h = document.createElement('h1');
  h.textContent = 'That page could not be loaded';
  const p = document.createElement('p');
  p.textContent =
    'This usually means the site was updated while your browser had the old version ' +
    'open. Reloading the page should fix it.';
  const detail = document.createElement('p');
  detail.className = 'field-note';
  detail.textContent = String(err?.message ?? err);
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'button';
  b.textContent = 'Reload';
  b.addEventListener('click', () => location.reload());
  box.append(h, p, b, detail);
  mount.append(box);
}

function renderPageError(err) {
  const box = document.createElement('div');
  box.className = 'notice notice--warning';
  const h = document.createElement('h2');
  h.textContent = 'Something went wrong drawing this page';
  const p = document.createElement('p');
  p.className = 'field-note';
  p.textContent = String(err?.message ?? err);
  box.append(h, p);
  mount.append(box);
}
